import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../angular-wallet-base/store/appState';
import { ChangeTitleAction, ChangeSecondatyTitleAction, UseI18nOnTitleAction } from '../../../angular-wallet-base/actions/appSettings.actions';
import { AlertController, LoadingController, Platform, ModalController } from '@ionic/angular';
import { NavController } from '@ionic/angular';
import { EpinManagerService } from '../../../angular-wallet-base/services/epin-manager.service';
import { TranslateService } from '@ngx-translate/core';
import { Logger } from '../../../angular-wallet-base/services/logger.service';
import { MembersPortalService } from '../../../angular-wallet-base/services/members-portal.service';
import { AGX_GUID } from '../../../angular-wallet-base/constants';
import { PriceOracleService } from '../../../angular-wallet-base/services/price-oracle.service';
import BigNumber from 'bignumber.js';
import {CreditCardValidators} from 'angular-cc-library';
import { ActivatedRoute } from '@angular/router';
import { checkNumberLimit, checkValidCardExpiry, formatMonth, isEnterKey } from '../../../angular-wallet-base/utils';
import { createKeyringId } from '../../../angular-wallet-base/keyringUtils';
import { supportedAssetList } from '../../../angular-wallet-base/lib/supported-assets/supported-assets';
import { PriceTiersComponent } from '../../../components/price-tiers/price-tiers.component';
import { AppModeService } from '../../../angular-wallet-base/services/app-mode.service';
import { ComplianceService } from 'src/app/angular-wallet-base/services/compliance.service';
@Component({
  selector: 'app-billing-form',
  templateUrl: './billing-form.component.html',
  styleUrls: ['./billing-form.component.scss'],
})
export class BillingFormComponent implements OnInit, OnDestroy {
  public billingForm: FormGroup;
  public countries;
  private membersPortalUserInfo;
  public usdAmount;
  public tokenFiatValue;
  public oldTokenFiatValue;
  public hideForm: boolean = false;
  private loadingModal;
  private ccIframeIsOpen: boolean = false;
  private getTokenSuccess: boolean;
  private iframeInterval;
  private getQuoteTimeout;
  public regions = [];
  public maxTokens = {
    aux: 0,
    agx: 0
  };
  public maxExceeded;
  public tokenSymbol;
  public tokenGuid;
  public baseChainSymbol;
  public tokenInfo;
  public tokenBacking;
  public feeAmount = 0;
  public subFeeAmount = 0;
  public convenienceFeeAmount = 0;
  public totalAmount = 0;
  public promoCodeList = [];
  public promoCodeError = false;
  private quoteId;
  public removeCleaner = true;
  public discount = 0;
  public promoErrorMessage = '';
  public totalAmountMaxExceededError = true
  public totalAmountMaxExceededErrorMessage = ''
  public isDesktopMode;

  constructor(
    private formBuilder: FormBuilder,
    private epin: EpinManagerService,
    private alertController: AlertController,
    public nav: NavController,
    private translate: TranslateService,
    private members: MembersPortalService,
    private complianceService: ComplianceService,
    private store: Store<AppState>,
    private loadingController: LoadingController,
    private appMode: AppModeService,
    private agxOraclePriceService: PriceOracleService,
    private activatedRoute: ActivatedRoute,
    private platform: Platform,
    private modal: ModalController
  ) {
    this.billingForm = this.formBuilder.group({
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      address: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      zip: ['', Validators.required],
      country: ['', Validators.required],
      phone: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      promoCode: [''],
      tokenAmount: ['', Validators.required],
      amount: [0, [Validators.required]],
      // CC data
      cc_number: ['', [Validators.required, CreditCardValidators.validateCCNumber]],
      cc_month: ['', [Validators.required]],
      cc_year: ['', [Validators.required]],
      cc_cvv: ['', [Validators.required, checkNumberLimit(3,4)]]
    });
    this.appMode.isDesktopMode.subscribe(mode => this.isDesktopMode = mode);
  }

  async ngOnInit() {
    this.baseChainSymbol = this.activatedRoute.snapshot.queryParams.baseChainSymbol;
    this.tokenGuid = this.activatedRoute.snapshot.queryParams.guid;
    this.tokenSymbol = this.activatedRoute.snapshot.queryParams.symbol;
    this.tokenBacking = this.tokenGuid === AGX_GUID ? (await this.translate.get('buy_agx.silver_label').toPromise()) : (await this.translate.get('buy_agx.gold_label').toPromise());

    const loadingModal = await this.generateLoadingModal();
    await loadingModal.present();

    try {
      this.maxTokens = await this.epin.getMaxRedeemableTokens();
    } catch (err) {
      Logger.error(err);
    }

    this.billingForm.get('tokenAmount').valueChanges.subscribe(val => this.onTokenAmountChange(val));
    // TODO: Not required API calls
    const oraclePrices = await this.agxOraclePriceService.getValueInBaseCurrency([this.getActiveKeyringId()], ['USD'], true);
    const markup = await this.agxOraclePriceService.getMarkup(this.tokenSymbol.toLowerCase()) as number;
    const tokenFiat = new BigNumber(oraclePrices.fiatValues[this.getActiveKeyringId()]).multipliedBy(markup);

    // get the token fiat value fresh from the backend using promocode stuff
    this.onTokenAmountChange(1, '');

    this.billingForm.controls.amount.setValidators([Validators.min(this.tokenFiatValue)]);

    this.countries = await this.members.getCountries();
    this.membersPortalUserInfo = await this.members.getUserInfo();

    if (this.membersPortalUserInfo.prov && this.membersPortalUserInfo.prov.id) {
      this.regions = await this.members.getRegions(this.membersPortalUserInfo.country.id);
    }

    this.setMembersPortalInfoInForm();

    await loadingModal.dismiss();

    // This intervals detects if payment modal is up and sets UI state accordingly
    this.iframeInterval = setInterval(() => {
      const ccIframe = document.querySelector('iframe');

      if (ccIframe && !this.ccIframeIsOpen) {
        this.ccIframeIsOpen = true;
      }

      if (!ccIframe && this.ccIframeIsOpen) {
        this.ccIframeIsOpen = false;

        if (!this.getTokenSuccess) {
          this.hideForm = false;

          if (this.loadingModal) {
            this.loadingModal.dismiss();
          }
        }
      }
    }, 300);

    this.tokenInfo = supportedAssetList[this.getActiveKeyringId()];
  }
  getActiveKeyringId() {
    return createKeyringId(this.baseChainSymbol, this.tokenGuid);
  }

  async generateLoadingModal() {
    return this.loadingController.create({
      message: await this.translate.get('buy_agx.loading.please_wait').toPromise(),
      duration: 9000000
    });
  }

  async ngAfterViewInit() {
    const i18nTitle = await this.translate.get('buy_agx.title_one', { tokenSymbol: this.tokenSymbol.toUpperCase() }).toPromise();
    const i18nSecondaryTitle = await this.translate.get('buy_agx.title_two').toPromise();
    this.store.dispatch(new ChangeTitleAction(i18nTitle));
    this.store.dispatch(new ChangeSecondatyTitleAction(i18nSecondaryTitle));
    this.store.dispatch(new UseI18nOnTitleAction(false));
  }

  onTokenAmountBlur($event) {
    const amount = this.billingForm.get('tokenAmount').value;
    const code = this.billingForm.get('promoCode').value;
    this.onTokenAmountChange(amount, code, '1');
  }

  onTokenAmountChange(amount, code: string = null, readOnly: string = '1') {
    // clear the previous timeout to prevent additional requests
    clearTimeout(this.getQuoteTimeout);
    const FETCH_QUOTE_TIMEOUT = 500;
    // These consts are used to filter the retreived added fees from getpricequote endpoint
    const subFeeAttribute = this.tokenSymbol.toLowerCase()+'_silvercard_under_25_fee';
    const convenienceFeeAttribute = this.tokenSymbol.toLowerCase()+'_silvercard_processing_fee'
    if (!amount) {
      // amount = 0;
      this.usdAmount = 0;
      return;
    }

    this.getQuoteTimeout = setTimeout(async () => {
      // Set timeout to prevent too many requests.
      let quote: any;

      try {
        quote = await this.members.getPriceQuote(amount, code ? code : this.promoCodeList[0], readOnly, this.tokenSymbol);
        this.quoteId = quote.data.quote_id;

        if (quote.data.promo_code) {
          const promoCodeControl = this.billingForm.get('promoCode');

          if (code.trim()) {
            this.promoCodeList = [code] 
          }
          promoCodeControl.setValue('');
        }
      } catch(err) {
        if(err.error.error) {
          this.promoCodeError = true;
          let errorMessage = await this.translate.get(`buy_agx.billing_form.promocode.${err.error.error.split(':')[0]}`, { tokenSymbol: this.tokenInfo.symbol }).toPromise()

          if (errorMessage.includes('[MISSING]')) {
            errorMessage = await this.translate.get('buy_agx.billing_form.promocode.invalid').toPromise()
          }
     
          this.promoErrorMessage = errorMessage
        } 
        return;
      }

      this.tokenFiatValue = Number(quote.data.ppt);
      this.oldTokenFiatValue = null;

      this.maxExceeded = amount > this.maxTokens[this.tokenSymbol.toLowerCase()];
      const bgAmount = new BigNumber(amount);
      this.billingForm.get('amount').setValue(bgAmount.multipliedBy(this.tokenFiatValue));
      const formTokenAmount = this.billingForm.get('tokenAmount').value;
      if (Number(amount) === Number(formTokenAmount)) {
        // CLIENT SIDE CALC - deprecated
        // this.usdAmount = bgAmount.multipliedBy(this.tokenFiatValue);
        // this.feeAmount = this.epin.addFees(this.usdAmount) - this.usdAmount;
        // this.totalAmount = this.epin.addFees(this.usdAmount);

        // SERVER SIDE CALC - use server side values instead of calculating client side
        // quote.data.price_total
        // quote.data.price_total_before_discount
        // quote.data.price_total_before_fees
        this.subFeeAmount = quote.data.added_fees
                  .filter(fee => (fee.name === subFeeAttribute))
                  .map(({ fee_amount }) => fee_amount)[0];
        this.convenienceFeeAmount = quote.data.added_fees
                  .filter(fee => (fee.name === convenienceFeeAttribute))
                  .map(({ fee_amount }) => fee_amount)[0];
        this.feeAmount = Number(quote.data.fee_amount);
        this.usdAmount = new BigNumber(quote.data.price_total_before_fees);
        
        this.totalAmount = Number(quote.data.price_total);
        this.totalAmountMaxExceededError = false;
        this.totalAmountMaxExceededErrorMessage = ''

        if (this.totalAmount > 2000) {
          this.totalAmountMaxExceededError = true
          this.totalAmountMaxExceededErrorMessage = await this.translate.get('buy_agx.billing_form.max_purchase_purchase_exceeded').toPromise()
        }
        
        this.discount = quote.data.discount;
        const discountType = quote.data.applied_discount ? quote.data.applied_discount.discount_type : null

        if (discountType === 'percent_premium' && quote.data.ppt_before_discount && quote.data.ppt_before_discount !== quote.data.ppt) {
          this.oldTokenFiatValue = Number(quote.data.ppt_before_discount);
          this.tokenFiatValue = Number(quote.data.ppt);
        }

      } else {
        this.usdAmount = 0;
      }
    }, FETCH_QUOTE_TIMEOUT);
  }

  setMembersPortalInfoInForm() {
    const userInfo = this.membersPortalUserInfo;

    this.billingForm.get('firstname').setValue(userInfo.fname || '');
    this.billingForm.get('lastname').setValue(userInfo.lname || '');
    this.billingForm.get('address').setValue(userInfo.address || '');
    this.billingForm.get('city').setValue(userInfo.city || '');
    this.billingForm.get('zip').setValue(userInfo.postal || '');
    this.billingForm.get('phone').setValue(userInfo.phone || '');
    this.billingForm.get('email').setValue(userInfo.email || '');

    if (userInfo.country && userInfo.country.id) {
      this.billingForm.get('country').setValue(userInfo.country.id || '');
    }
    if (userInfo.prov && userInfo.prov.id) {
      this.billingForm.get('state').setValue(userInfo.prov.id || '');
    }
  }

  async onChangeCountry(e) {
    const id = e.target.value;

    if (!id) {
      this.regions = [];
      return;
    }

    const loadingModal  = await this.generateLoadingModal();

    loadingModal.present();

    this.regions = await this.members.getRegions(e.target.value);

    loadingModal.dismiss();
  }

  clearForm() {
    this.billingForm.get('firstname').setValue('');
    this.billingForm.get('lastname').setValue('');
    this.billingForm.get('address').setValue('');
    this.billingForm.get('city').setValue('');
    this.billingForm.get('state').setValue('');
    this.billingForm.get('country').setValue('');
    this.billingForm.get('zip').setValue('');
    this.billingForm.get('phone').setValue('');
    this.billingForm.get('email').setValue('');
    this.billingForm.get('tokenAmount').setValue('');
    this.billingForm.get('amount').setValue(0);
    this.billingForm.get('cc_number').setValue('')
    this.billingForm.get('cc_month').setValue('')
    this.billingForm.get('cc_year').setValue('')
    this.billingForm.get('cc_cvv').setValue('')
    this.promoCodeList = [];
    this.billingForm.markAsPristine();
    this.billingForm.markAsUntouched();
    this.billingForm.clearValidators();
  }

  ngOnDestroy() {
    if (this.loadingModal) {
      this.loadingModal.dismiss();
    }

    if (this.iframeInterval) {
      clearInterval(this.iframeInterval);
    }
  }

  async onSubmit() {
    let epin;

    this.loadingModal = await this.generateLoadingModal();
    await this.loadingModal.present();


    let initials = this.billingForm.get('firstname').value.charAt(0).toLowerCase();
    let lastName = this.billingForm.get('lastname').value.toLowerCase();

    let res:any = await this.complianceService.getKycCipData();
    if(!res.passingLevel)
    {
      this.loadingModal.dismiss();
      this.hideForm = false;
      const alert = await this.alertController.create({
        header: await this.translate.get('buy_agx.billing_form.error_header').toPromise(),
        message: await this.translate.get('buy_agx.billing_form.error_kyc').toPromise(),
        cssClass: "modal-fullscreen"
      });
      alert.present();
      return;
    }

    this.hideForm = true;
    this.getTokenSuccess = true;
    const countryCode = this.countries.find(country => country.id === this.billingForm.get('country').value).code;
    const stateName = this.regions.find(state => state.id === this.billingForm.get('state').value).name;

    const opts = {
      tokenSymbol: this.tokenSymbol.toLowerCase(),
      firstname: this.billingForm.get('firstname').value,
      lastname: this.billingForm.get('lastname').value,
      address: this.billingForm.get('address').value,
      city: this.billingForm.get('city').value,
      state: stateName,
      zip: this.billingForm.get('zip').value,
      country: countryCode,
      phone: this.billingForm.get('phone').value,
      email: this.billingForm.get('email').value,
      amount: this.billingForm.get('tokenAmount').value,
      ccNumber: this.billingForm.get('cc_number').value,
      ccMonth: formatMonth(this.billingForm.get('cc_month').value.toString()),
      ccYear: this.billingForm.get('cc_year').value.toString(),
      ccCvv: this.billingForm.get('cc_cvv').value,
      promoCode: this.promoCodeList[0],
      quoteId: this.quoteId
    };

    try {
      epin = await this.epin.generateEpin(opts);
    } catch (err) {
      Logger.error(err);
      let errorMessage;

      try {
        // Tries to get error reason from response, if can't find any, fallback to generic error.
        errorMessage = err.error.message ? err.error.message : err.error.error.result.responsetext;

        if (!errorMessage) {
          throw new Error();
        }
      } catch (err) {
        errorMessage = await this.translate.get('buy_agx.billing_form.error_description').toPromise()
      }

      this.loadingModal.dismiss();
      this.hideForm = false;
      const alert = await this.alertController.create({
        header: await this.translate.get('buy_agx.billing_form.error_header').toPromise(),
        message: errorMessage,
        cssClass: "modal-fullscreen"
      });
      alert.present();
      return;
    }

    const epinNumber = epin.result.epin;
    this.loadingModal.dismiss();

    this.hideForm = false;
    this.nav.navigateForward(['/wallet/buy-tokens/step-three', epinNumber.toString()], { queryParams: { hidePurchaseText: true, symbol: this.tokenSymbol, guid: this.tokenGuid, baseChainSymbol: this.baseChainSymbol } });
  }

  togglePromoCode(code: string) {
    if (this.promoCodeList.indexOf(code) !== -1) {
      this.promoCodeList = this.promoCodeList.filter(val => val !== code);
      this.onTokenAmountChange(this.billingForm.get('tokenAmount').value, this.billingForm.get('promoCode').value);
    } else {
      this.onTokenAmountChange(this.billingForm.get('tokenAmount').value, this.billingForm.get('promoCode').value);
    }
  }

  get isIos() {
    return this.platform.is('ios');
  }
  onRequiredFieldChange(ev) {
    const inputElement = ev.target;
    let span = inputElement.previousSibling;
    let style = "";
    if (inputElement.value !== ''){
      style = "none";
    }
    span.style.display = style;
  }
  async onKeyUp(ev) {
    if (isEnterKey(ev) && !this.isSubmitDisabled()) {
      this.onSubmit();
    }
  }

  isSubmitDisabled() {
    return !this.billingForm.valid || this.maxExceeded || this.totalAmountMaxExceededError || this.isCardExpired();
  }
  isCardExpired() {
    return checkValidCardExpiry(this.billingForm.get('cc_month').value, this.billingForm.get('cc_year').value);
  }
  isMonthExpired(){
    const d = new Date();
    const m = d.getMonth();
    const y = d.getFullYear();
    const lastTwoDigits = parseInt(`${y}`.substr(2));
    const input_month = this.billingForm.get("cc_month").value;
    const input_year = this.billingForm.get("cc_year").value;
    if (parseInt(input_month) > 12 || parseInt(input_month) < 1){
      return true;
    }
    return input_year && parseInt(input_year) === lastTwoDigits && parseInt(input_month) < m + 1;
  }
  isYearExpired(){
    const d = new Date();
    const y = d.getFullYear();
    const lastTwoDigits = parseInt(`${y}`.substr(2));
    const input_year = this.billingForm.get("cc_year").value;
    return input_year && parseInt(input_year) < lastTwoDigits;
  }
  isPromoCodeDisabled() {
    return !this.billingForm.get('promoCode').value.trim()
  }

  async openPriceTiers(isMobile) {
    const modal = await this.modal.create({
      component: PriceTiersComponent,
      componentProps: {
        amount: this.billingForm.get('tokenAmount').value,
        tokenSymbol: this.tokenSymbol,
        mode: isMobile ? 'mobile' : 'desktop'
      }
    });
    await modal.present();
  }
  
  validatePromoError() {
    
    if(this.billingForm.get('promoCode').value === ''){
      this.promoCodeError = false;
      this.promoErrorMessage = ''
    }

    if(this.promoCodeError) {
      return true;
    }
    return false;
  }

}
